import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Award, Bell, Trophy, Medal, Star, Users, ClipboardCheck, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '--';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const Appraisals = () => {
  const [cycles, setCycles] = useState([]);
  const [activeTab, setActiveTab] = useState('cycles');
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [results, setResults] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);

  const loadCycles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/appraisals/cycles`);
      setCycles(r.data || []);
      if (!selectedCycle && (r.data || []).length > 0) {
        setSelectedCycle(r.data[0]);
      }
    } catch (e) { console.error(e); }
  }, [selectedCycle]);

  useEffect(() => { loadCycles(); }, [loadCycles]);

  const loadResults = useCallback(async (cycleId) => {
    if (!cycleId) return;
    setLoadingResults(true);
    try {
      const [r, l] = await Promise.all([
        axios.get(`${API}/api/appraisals/results/${cycleId}`),
        axios.get(`${API}/api/appraisals/leaderboard/${cycleId}?limit=3`),
      ]);
      setResults(r.data || []);
      setLeaderboard(l.data || []);
    } catch (e) { console.error(e); } finally { setLoadingResults(false); }
  }, []);

  useEffect(() => {
    if (selectedCycle?.id && (activeTab === 'results' || activeTab === 'leaderboard')) {
      loadResults(selectedCycle.id);
    }
  }, [selectedCycle, activeTab, loadResults]);

  const closeCycle = async (id) => {
    if (!window.confirm('Close this cycle? No more reviews can be submitted.')) return;
    await axios.patch(`${API}/api/appraisals/cycles/${id}`, { status: 'closed' });
    toast({ title: 'Cycle closed' });
    loadCycles();
  };

  const reopenCycle = async (id) => {
    await axios.patch(`${API}/api/appraisals/cycles/${id}`, { status: 'active' });
    toast({ title: 'Cycle re-opened' });
    loadCycles();
  };

  const deleteCycle = async (id) => {
    if (!window.confirm('Delete this cycle and all its reviews? This cannot be undone.')) return;
    await axios.delete(`${API}/api/appraisals/cycles/${id}`);
    setSelectedCycle(null);
    setResults([]); setLeaderboard([]);
    loadCycles();
    toast({ title: 'Cycle deleted' });
  };

  const sendReminder = async (id) => {
    try {
      const r = await axios.post(`${API}/api/appraisals/cycles/${id}/notify-pending`);
      toast({ title: 'Reminders sent', description: `${r.data.managers_notified} manager(s) notified` });
    } catch (e) { toast({ title: 'Error', description: 'Failed to send reminders', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-6" data-testid="appraisals-page">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Award className="h-7 w-7 text-amber-500" /> Employee Appraisals</h1>
          <p className="text-gray-600 mt-1">Run review cycles, track participation and recognise top performers</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="appraisal-new-cycle">
          <Plus className="h-4 w-4 mr-1" /> New Cycle
        </Button>
      </div>

      {/* Cycle Picker */}
      {cycles.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="cycle-picker">
          {cycles.map(c => (
            <button
              key={c.id}
              onClick={() => setSelectedCycle(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition border ${selectedCycle?.id === c.id ? 'bg-amber-500 text-white border-amber-500 shadow-md' : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'}`}
              data-testid={`cycle-pill-${c.id}`}
            >
              {c.name} <span className="ml-1 opacity-70">({c.status})</span>
            </button>
          ))}
        </div>
      )}

      {/* Tabs */}
      {selectedCycle && (
        <div className="flex gap-1 border-b" data-testid="appraisal-tabs">
          {[
            { k: 'cycles', label: 'Cycle Details', icon: ClipboardCheck },
            { k: 'results', label: 'Results', icon: Users },
            { k: 'leaderboard', label: 'Leaderboard', icon: Trophy },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setActiveTab(t.k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${activeTab === t.k ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              data-testid={`tab-${t.k}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {!selectedCycle && cycles.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Award className="h-10 w-10 mx-auto text-amber-400 mb-3" />
            <p className="text-gray-600">No appraisal cycles yet. Click <strong>New Cycle</strong> to start.</p>
          </CardContent>
        </Card>
      )}

      {selectedCycle && activeTab === 'cycles' && (
        <CycleDetails
          cycle={selectedCycle}
          onEdit={() => setEditingCycle(selectedCycle)}
          onClose={() => closeCycle(selectedCycle.id)}
          onReopen={() => reopenCycle(selectedCycle.id)}
          onDelete={() => deleteCycle(selectedCycle.id)}
          onRemind={() => sendReminder(selectedCycle.id)}
        />
      )}

      {selectedCycle && activeTab === 'results' && (
        <ResultsTable rows={results} loading={loadingResults} />
      )}

      {selectedCycle && activeTab === 'leaderboard' && (
        <Leaderboard rows={leaderboard} loading={loadingResults} />
      )}

      {showCreate && (
        <CycleDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); loadCycles(); }}
        />
      )}

      {editingCycle && (
        <CycleDialog
          cycle={editingCycle}
          onClose={() => setEditingCycle(null)}
          onSaved={() => { setEditingCycle(null); loadCycles(); }}
        />
      )}
    </div>
  );
};

// ── Sub Components ─────────────────────────────────────────────────────

const CycleDetails = ({ cycle, onEdit, onClose, onReopen, onDelete, onRemind }) => (
  <Card data-testid="cycle-details-card">
    <CardHeader>
      <CardTitle className="flex items-center justify-between">
        <span>{cycle.name}</span>
        <Badge className={cycle.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}>
          {cycle.status}
        </Badge>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-amber-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-amber-600">Start</p>
          <p className="text-sm font-semibold mt-1">{formatDate(cycle.start_date)}</p>
        </div>
        <div className="bg-rose-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-rose-600">Deadline</p>
          <p className="text-sm font-semibold mt-1">{formatDate(cycle.deadline_date)}</p>
        </div>
        <div className="bg-sky-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-sky-600">Staff Qs</p>
          <p className="text-sm font-semibold mt-1">{cycle.staff_questions?.length || 0}</p>
        </div>
        <div className="bg-violet-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-violet-600">Manager Qs</p>
          <p className="text-sm font-semibold mt-1">{cycle.manager_questions?.length || 0}</p>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2 text-gray-700">Staff Self-Review Questions (AI-scored)</h4>
        <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-600">
          {(cycle.staff_questions || []).map((q, i) => <li key={i}>{q}</li>)}
        </ol>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2 text-gray-700">Manager Review Questions (5 marks each)</h4>
        <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-600">
          {(cycle.manager_questions || []).map((q, i) => <li key={i}>{q}</li>)}
        </ol>
      </div>

      <div className="flex flex-wrap gap-2 pt-3 border-t">
        <Button variant="outline" size="sm" onClick={onEdit} data-testid="cycle-edit-btn">Edit</Button>
        <Button variant="outline" size="sm" onClick={onRemind} data-testid="cycle-remind-btn">
          <Bell className="h-4 w-4 mr-1" /> Send Reminder to Pending Managers
        </Button>
        {cycle.status === 'active'
          ? <Button variant="outline" size="sm" onClick={onClose}>Close Cycle</Button>
          : <Button variant="outline" size="sm" onClick={onReopen}>Re-open Cycle</Button>}
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
          <Trash2 className="h-4 w-4 mr-1" /> Delete
        </Button>
      </div>
    </CardContent>
  </Card>
);

const ResultsTable = ({ rows, loading }) => {
  if (loading) return <Card><CardContent className="py-10 text-center text-gray-500">Loading…</CardContent></Card>;
  if (!rows.length) return <Card><CardContent className="py-10 text-center text-gray-500">No reviews submitted yet</CardContent></Card>;
  return (
    <Card data-testid="results-card">
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-amber-50 text-amber-800 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">Employee</th>
              <th className="text-left p-3">Department</th>
              <th className="text-center p-3">Self %</th>
              <th className="text-center p-3">Manager %</th>
              <th className="text-center p-3">Combined /10</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.employee_id} className="border-t hover:bg-amber-50/30" data-testid={`result-row-${r.employee_id}`}>
                <td className="p-3 text-gray-500">{i + 1}</td>
                <td className="p-3">
                  <div className="font-semibold text-gray-800">{r.employee_name}</div>
                  <div className="text-[11px] text-gray-500">{r.employee_code}</div>
                </td>
                <td className="p-3 text-gray-600">{r.department || '—'}</td>
                <td className="p-3 text-center">{r.self_percentage != null ? `${r.self_percentage}%` : <span className="text-gray-400">—</span>}</td>
                <td className="p-3 text-center">{r.manager_percentage != null ? `${r.manager_percentage}%` : <span className="text-gray-400">—</span>}</td>
                <td className="p-3 text-center">
                  <span className="font-bold text-amber-700">{r.combined_score_out_of_10}</span>
                  <span className="text-gray-400 text-xs">/10</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

const Leaderboard = ({ rows, loading }) => {
  if (loading) return <Card><CardContent className="py-10 text-center text-gray-500">Loading…</CardContent></Card>;
  if (!rows.length) return (
    <Card><CardContent className="py-10 text-center text-gray-500">
      Top performers appear once at least one employee has both self-review and manager-review completed.
    </CardContent></Card>
  );
  const trophies = [
    { icon: Trophy, color: 'text-yellow-500', bg: 'from-yellow-50 to-amber-100', label: '1st Place' },
    { icon: Medal, color: 'text-slate-500', bg: 'from-slate-50 to-slate-100', label: '2nd Place' },
    { icon: Star, color: 'text-orange-500', bg: 'from-orange-50 to-amber-50', label: '3rd Place' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="leaderboard">
      {rows.map((r, i) => {
        const T = trophies[i] || trophies[2];
        const I = T.icon;
        return (
          <Card key={r.employee_id} className={`border-2 bg-gradient-to-br ${T.bg}`} data-testid={`top-${i + 1}`}>
            <CardContent className="p-6 text-center">
              <I className={`h-12 w-12 mx-auto mb-3 ${T.color}`} />
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{T.label}</p>
              <h3 className="text-lg font-bold text-gray-900">{r.employee_name}</h3>
              <p className="text-xs text-gray-500 mt-1">{r.employee_code} • {r.department}</p>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <div><p className="font-bold text-emerald-700">{r.self_percentage}%</p><p className="text-gray-400">Self</p></div>
                <div><p className="font-bold text-violet-700">{r.manager_percentage}%</p><p className="text-gray-400">Manager</p></div>
                <div><p className="font-bold text-amber-700">{r.combined_score_out_of_10}/10</p><p className="text-gray-400">Combined</p></div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ── Create/Edit Cycle Dialog ───────────────────────────────────────────

const DEFAULT_STAFF_Q = [
  "How would you rate your manager's work ethics and behavior toward you? Please share details.",
  "How satisfied are you with the quality of work assigned to you? What works and what doesn't?",
  "What changes do you suggest to improve productivity and efficiency at work?",
  "Overall, how is your job satisfaction and what would make it better?",
];
const DEFAULT_MGR_Q = [
  "Overall job performance",
  "Quality of work / attention to detail",
  "Behavior on the floor / attitude towards peers",
  "Issues / concerns observed (if any)",
  "Punctuality, reliability and ownership",
];

const CycleDialog = ({ cycle, onClose, onSaved }) => {
  const editing = !!cycle;
  const [name, setName] = useState(cycle?.name || `Appraisal ${new Date().toLocaleString('default', { month: 'short', year: 'numeric' })}`);
  const [start, setStart] = useState(cycle?.start_date || new Date().toISOString().slice(0, 10));
  const [deadline, setDeadline] = useState(cycle?.deadline_date || new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10));
  const [staffQ, setStaffQ] = useState(cycle?.staff_questions || DEFAULT_STAFF_Q);
  const [managerQ, setManagerQ] = useState(cycle?.manager_questions || DEFAULT_MGR_Q);
  const [saving, setSaving] = useState(false);

  const updateQ = (list, setList, idx, val) => {
    const copy = [...list]; copy[idx] = val; setList(copy);
  };

  const submit = async () => {
    if (!name.trim()) return toast({ title: 'Name required', variant: 'destructive' });
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        start_date: start,
        deadline_date: deadline,
        staff_questions: staffQ.filter(q => q.trim()),
        manager_questions: managerQ.filter(q => q.trim()),
      };
      if (editing) await axios.patch(`${API}/api/appraisals/cycles/${cycle.id}`, body);
      else await axios.post(`${API}/api/appraisals/cycles`, body);
      toast({ title: editing ? 'Cycle updated' : 'Cycle created' });
      onSaved();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cycle-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit Cycle' : 'New Appraisal Cycle'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Cycle Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="cycle-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="cycle-start" />
            </div>
            <div>
              <Label className="text-xs">Deadline</Label>
              <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} data-testid="cycle-deadline" />
            </div>
          </div>

          <QuestionEditor
            label="Staff Self-Review Questions (AI-scored, 0–5 each)"
            list={staffQ}
            onChange={(i, v) => updateQ(staffQ, setStaffQ, i, v)}
            onAdd={() => setStaffQ([...staffQ, ''])}
            onRemove={(i) => setStaffQ(staffQ.filter((_, x) => x !== i))}
            testid="staff-q"
          />
          <QuestionEditor
            label="Manager Review Questions (5 marks each)"
            list={managerQ}
            onChange={(i, v) => updateQ(managerQ, setManagerQ, i, v)}
            onAdd={() => setManagerQ([...managerQ, ''])}
            onRemove={(i) => setManagerQ(managerQ.filter((_, x) => x !== i))}
            testid="mgr-q"
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="cycle-save">
              {saving ? 'Saving…' : editing ? 'Update' : 'Create & Notify'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QuestionEditor = ({ label, list, onChange, onAdd, onRemove, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="space-y-2 mt-1">
      {list.map((q, i) => (
        <div key={i} className="flex gap-2">
          <span className="pt-2 text-xs text-gray-500 w-5">{i + 1}.</span>
          <Textarea
            value={q}
            onChange={e => onChange(i, e.target.value)}
            rows={2}
            className="text-sm"
            data-testid={`${testid}-${i}`}
          />
          <Button type="button" variant="ghost" size="icon" onClick={() => onRemove(i)} className="h-9 w-9 text-rose-500">
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={onAdd}>
        <Plus className="h-3 w-3 mr-1" /> Add Question
      </Button>
    </div>
  </div>
);

export default Appraisals;
